import { Injectable } from '@angular/core';
import groupBy from 'lodash-es/groupBy';
import sortBy from 'lodash-es/sortBy';
import isEmpty from 'lodash-es/isEmpty';
import get from 'lodash-es/get';
import pick from 'lodash-es/pick';
import { ISortOptions } from '@finder/features/list/services/sort/sort-options.interface';

@Injectable()
export class SortService {

    private cleanedValues = {};

    constructor() { }

    sort(options: ISortOptions): any {
        const { type, withAvailability, list, featuredIds, ordering } = options;

        if (withAvailability) {
            return this.sortByAvailability(type, list, featuredIds);
        }

        return this.sortByType(type, list, featuredIds, ordering);
    }

    private sortByAvailability(type, list, featuredIds): any {
        list = groupBy(list, item => {
            if (item.hasAvailability === true) {
                return 'available';
            }

            if (item.hasAvailability === false) {
                return 'notAvailable';
            }

            return 'otherResults';
        });

        // we need to sort the list keys, that way we make sure
        // that 'available' results render first
        return Object.keys(list).sort().reduce((results, group) => {
            results[group] = this.sortByType(type, list[group], featuredIds);

            return results;
        }, {});
    }

    private sortByType(type, list, featuredIds, ordering?) {
        switch (type) {
            default:
            case 'name':
                return this.sortByName(list);
            case 'location':
                return this.sortByLocation(list);
            case 'featured':
                return this.sortByFeatured(list, featuredIds);
            case 'type':
                return this.sortByDestinationType(list, ordering);
            case 'waitTime':
                return this.sortByWaitTime(list);
        }
    }

    private sortByName(list: Array<any>): any {
        list.sort(this.cleanNames.bind(this));

        return groupBy(list, item => item.closedForRefurbishment ? 'closedForRefurbishment' : 'all');
    }

    private sortByLocation(list: Array<any>): any {
        const sortedList = sortBy(list, ['locationName', 'landName']);

        return groupBy(sortedList, item => `${item.locationName}-${item.landName}`);
    }

    private sortByDestinationType(list: Array<any>, ordering?: Array<string>): any {
        const sortedList = sortBy(list, 'name');
        const groupedResult = groupBy(sortedList, item => item.entityType);

        if (ordering) {
            ordering.forEach((element, i) => {
                if (groupedResult[element]) {
                    groupedResult[i] = groupedResult[element];
                    delete groupedResult[element];
                }
            });
        }

        return groupedResult;
    }

    private sortByFeatured(list: Array<any>, featuredIds: Array<any>): any {
        const result: any = {};
        const filteredList = this.groupByClosedForRefurbishment(list);

        const listByFeaturedIds = this.getListByIds(filteredList.all, featuredIds);

        // check if we found the featured items in the list
        // and add them to the result.
        if (listByFeaturedIds.listByIds) {
            result.featured = listByFeaturedIds.listByIds;
        }
        // get the rest and sorted by name
        const sortedList = this.sortByName(listByFeaturedIds.rest);

        // if we have sorted by name items add them to the result.
        if (get(sortedList, 'all')) {
            result.featured = (result.featured || [])
                .concat(sortedList.all);
        }

        if (!isEmpty(filteredList.closedForRefurbishment)) {
            result.closedForRefurbishment = filteredList.closedForRefurbishment;
        }

        return result;
    }

    private sortByWaitTime(list: Array<any>): any {
        // Create a name to sort by in the background
        list.forEach(item => item.sortName = this.sortByNameCleanup(item.name));

        // Now sort by the wait time and use sortName as a tie breaker
        const sortedList = sortBy(list, ['waitTime', 'sortName']);

        // Group the results by park
        return groupBy(sortedList, item => `${item.locationName}-${item.landName}`);
    }

    private getListByIds(list: Array<any>, ids: Array<any>): any {
        const listByIds = ids.reduce((result, id) => {
            const el = list.find(item => item.id === id);

            if (el) {
                result.push(el);
            }

            return result;
        }, []);

        const rest = list.filter(item => !ids.includes(item.id));

        return {
            listByIds,
            rest
        };
    }

    private groupByClosedForRefurbishment(list: Array<any>): any {
        const groupedByRefurb = groupBy(list, item => item.closedForRefurbishment ? 'closedForRefurbishment' : 'all');

        return {
            closedForRefurbishment: get(groupedByRefurb, 'closedForRefurbishment', []),
            all: get(groupedByRefurb, 'all', [])
        };
    }

    private cleanNames(result, nextResult) {
        let current = pick(result, ['name', 'urlFriendlyId']);
        let next = pick(nextResult, ['name', 'urlFriendlyId']);

        // Clean values
        [current, next] = [current, next].map(value => {
            // Check for a cleaned value first
            if (this.cleanedValues[value.urlFriendlyId]) {
                return this.cleanedValues[value.urlFriendlyId];
            }

            // Clean up the sorting name
            value.name = this.sortByNameCleanup(value.name);

            // Store the cleaned value
            this.cleanedValues[value.urlFriendlyId] = value;

            return value;
        });

        return current.name.localeCompare(next.name);
    }

    private sortByNameCleanup(value: string): string {
        // Lowercase the name
        let result = value.toLowerCase().trim();

        // Now clean it up
        result = this.removePrecedingArticles(result);
        result = this.removeQuotesAndTags(result);

        return result;
    }

    /**
     * @name removePrecedingArticles
     * @description Remove "The ", "A", or "An" from the beginning of a string
     * @param value item to clean
     * @returns String
     */
    private removePrecedingArticles(value) {
        const regex = /^a\s|^an\s|^the\s+/;

        return value.replace(regex, '');
    }

    /**
     * @name removeQuotesAndTags
     * @description Remove quotes and HTML tags from anywhere within a string
     * @param value item to clean
     * @returns string
     */
    private removeQuotesAndTags(value) {
        // find and remove any single or double quotes (including smart quotes)
        // and any HTML tags (including attributes) from anywhere in the string.
        // https://asna.com/us/tech/kb/doc/remove-smart-quote
        const quoteAndTagRegex = /['|"|\u201C\u201D|\u2018\u2019]|<[^>]*>/g;

        return value.replace(quoteAndTagRegex, '');
    }
}
